<template>
    <v-dialog v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="640" :fullscreen="isMobile" persistent content-class="re-editor-dialog">
        <div class="pfe-panel-inner" :class="{ 'pfe-fullscreen': isMobile }">
            <div class="pfe-header">
                <v-btn icon small class="pfe-back pfe-back--close" @click="hide">
                    <v-icon size="16">mdi-close</v-icon>
                </v-btn>
                <span class="pfe-title">{{ title }}</span>
                <history-controls v-if="dialog && !loadingEdit" context="editor" />
                <div v-else style="width:32px"></div>
            </div>

            <div class="pfe-body">
                <skeleton-loader v-if="loadingEdit" type="rows" :count="5" />

                <template v-else>
                    <div class="pfe-section-title">{{ $t('steppers.blocks_and_exercises') }}</div>
                    <div v-if="blocks.length === 0" class="re-empty">{{ $t('routines.no_blocks_created') }}</div>
                    <draggable v-model="blocks" draggable=".pfeb-block" handle=".pfeb-drag" ghost-class="ghost">
                        <routine-block-item v-for="(block, i) in blocks" :key="`${block.uid}-${historyRev}`" :block="block" :index="i"
                            :open="expandedBlocks.includes(block.uid)" @toggle="toggleBlock(block.uid)"
                            @update="onBlockUpdate(i, $event)" @delete="confirmDeleteBlock(i)"
                            @duplicate="duplicateBlock(i)" @change-name="changeBlockName(i)" />
                    </draggable>
                    <div class="re-add-actions">
                        <v-btn text :color="accentColor" class="re-add-block" @click="addBlock">
                            <v-icon left size="16">mdi-plus</v-icon>{{ $t('common.new') }} {{ $tc('common.blocks', 1) }}
                        </v-btn>
                        <v-btn text :color="accentColor" class="re-add-block" @click="$refs.addExistingBlock.show()">
                            <v-icon left size="16">mdi-view-dashboard-outline</v-icon>{{ $t('common.from_library') }}
                        </v-btn>
                    </div>
                </template>
            </div>

            <div class="pfe-footer">
                <v-btn text tile class="pfe-btn-cancel" @click="hide">{{ $t('common.cancel') }}</v-btn>
                <v-btn tile depressed color="primary" class="pfe-btn-next black--text" :disabled="saveDisabled"
                    :loading="saving" @click="save">
                    {{ $t('common.save') }}
                </v-btn>
            </div>

            <change-name-dialog ref="name" is-second-dialog @save="onNameSave" />
            <kimsa-confirm ref="confirmDeleteBlock" is-second-dialog alert-mode :title="$t('common.delete_block')"
                :content="$t('common.confirm_deleted_block')" @confirm="doDeleteBlock"
                @cancel="pendingDeleteBlock = null" />
            <add-existing-block-dialog ref="addExistingBlock" is-second-dialog :coach-id="coachId"
                @select="onSelectExistingBlock" />
        </div>
    </v-dialog>
</template>

<script>
import { db } from "@/utils/Firebase";
import { getMergedBlocks, clearBlocksAssignedRoutine } from "@/utils/Exercises";
import { randomString } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import RoutineBlockItem from "@/components/coach/routines/RoutineBlockItem";
import ChangeNameDialog from "@/components/coach/routines/ChangeNameDialog";
import AddExistingBlockDialog from "@/components/coach/routines/AddExistingBlockDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import draggable from "vuedraggable";
import HistoryControls from "@/components/common/HistoryControls";
import editorHistoryMixin from "@/mixins/editorHistoryMixin";

export default {
    name: "ProgramRoutineEditor",
    components: {
        SkeletonLoader, RoutineBlockItem, ChangeNameDialog, AddExistingBlockDialog, KimsaConfirm, draggable,
        HistoryControls,
    },
    mixins: [editorHistoryMixin],
    data() {
        return {
            dialog: false,
            saving: false,
            loadingEdit: false,
            program: null,
            programId: null,
            routineId: null,
            isNew: false,
            blocks: [],
            expandedBlocks: [],
            pendingDeleteBlock: null,
        }
    },
    computed: {
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        coachId(vm = this) {
            return vm.program?.coachId || null
        },
        title(vm = this) {
            if (vm.isNew) return vm.$t('routines.add_routine')
            return `${vm.$t('routines.edit_routine')} ${Number(vm.routineId || 0) + 1}`
        },
        saveDisabled(vm = this) {
            if (vm.saving || vm.loadingEdit) return true
            if (vm.blocks.length === 0) return true
            return vm.blocks.some(block => {
                if (block.exercises.length === 0) return true
                return block.exercises.some(ex => ex.type !== 'rest' && !vm.exerciseConfigured(block, ex))
            })
        },
    },
    methods: {
        exerciseConfigured(block, exercise) {
            if (block.isCircuit) {
                if (block.type === 'tabata') return true
                return !!(exercise?.config?.value || exercise?.config?.value === 0)
            }
            return exercise?.config?.sets?.length > 0
        },
        async show({ programId, routineId, create = false }) {
            const vm = this
            vm.reset()
            vm.programId = programId
            vm.routineId = routineId
            vm.isNew = create
            vm.dialog = true
            await vm.withLoading(async () => {
                const snap = await db.collection('programs').doc(programId).get()
                vm.program = snap.data()
                const routine = vm.program?.assignedRoutines?.[routineId] || null
                const merged = await getMergedBlocks(routine?.blocks || [])
                vm.blocks = merged.map(b => ({ ...b, uid: b.uid || randomString() }))
                if (vm.blocks.length === 0) vm.blocks = [{ uid: randomString(), exercises: [] }]
                vm.expandedBlocks = vm.blocks[0] ? [vm.blocks[0].uid] : []
                if (vm.dialog) vm.startEditorHistory('blocks')
            }, 'loadingEdit')
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.stopEditorHistory()
            vm.reset()
            vm.$emit('close')
        },
        reset() {
            const vm = this
            vm.saving = false
            vm.loadingEdit = false
            vm.program = null
            vm.programId = null
            vm.routineId = null
            vm.isNew = false
            vm.blocks = []
            vm.expandedBlocks = []
            vm.pendingDeleteBlock = null
        },
        toggleBlock(uid) {
            const vm = this
            const idx = vm.expandedBlocks.indexOf(uid)
            if (idx === -1) vm.expandedBlocks.push(uid)
            else vm.expandedBlocks.splice(idx, 1)
        },
        onBlockUpdate(i, block) {
            this.blocks.splice(i, 1, block)
        },
        addBlock() {
            const vm = this
            const block = { uid: randomString(), exercises: [] }
            vm.blocks.push(block)
            vm.expandedBlocks.push(block.uid)
        },
        async onSelectExistingBlock(hit) {
            const vm = this
            const merged = await getMergedBlocks([hit.block])
            const block = { ...merged[0], name: hit.name || null, uid: randomString() }
            vm.blocks.push(block)
            vm.expandedBlocks.push(block.uid)
        },
        confirmDeleteBlock(i) {
            const vm = this
            vm.pendingDeleteBlock = i
            vm.$refs.confirmDeleteBlock.show()
        },
        doDeleteBlock() {
            const vm = this
            if (vm.pendingDeleteBlock !== null) vm.deleteBlock(vm.pendingDeleteBlock)
            vm.pendingDeleteBlock = null
        },
        deleteBlock(i) {
            const vm = this
            const uid = vm.blocks[i] && vm.blocks[i].uid
            vm.blocks.splice(i, 1)
            const idx = vm.expandedBlocks.indexOf(uid)
            if (idx !== -1) vm.expandedBlocks.splice(idx, 1)
        },
        duplicateBlock(i) {
            const vm = this
            const copy = { ...JSON.parse(JSON.stringify(vm.blocks[i])), uid: randomString() }
            vm.blocks.splice(i + 1, 0, copy)
        },
        changeBlockName(i) {
            this.$refs.name.show(this.blocks[i], i)
        },
        onNameSave(payload) {
            const vm = this
            vm.blocks.splice(payload.index, 1, { ...vm.blocks[payload.index], name: payload.name })
        },
        async save() {
            const vm = this
            if (vm.saveDisabled) return
            await vm.withLoading(async () => {
                const assignedRoutines = [...(vm.program.assignedRoutines || [])]
                assignedRoutines[vm.routineId] = {
                    ...assignedRoutines[vm.routineId],
                    blocks: clearBlocksAssignedRoutine(vm.blocks)
                }
                await db.collection('programs').doc(vm.programId).update({ assignedRoutines })
                vm.toast(vm.$t('programs.saved'))
                vm.$emit('success', { routineId: vm.routineId, blocks: JSON.parse(JSON.stringify(vm.blocks)) })
                vm.hide()
            }, 'saving')
        },
    },
}
</script>

<style scoped>
.pfe-panel-inner {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 16px;
    overflow: hidden;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.pfe-fullscreen {
    height: 100%;
    max-height: none;
    border-radius: 0;
    border: none;
}

.pfe-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    flex-shrink: 0;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.pfe-fullscreen .pfe-header {
    padding-top: calc(14px + var(--safe-area-inset-top, 0px));
}

.pfe-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfe-back {
    background: rgba(232, 92, 92, 0.12) !important;
    color: var(--v-error-base) !important;
}

.pfe-back .v-icon {
    color: inherit !important;
}

.pfe-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (max-width: 959px) {
    .pfe-body::-webkit-scrollbar {
        display: none;
    }
}

.pfe-section-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.re-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 20px 0;
}

.re-add-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.re-add-block {
    text-transform: none !important;
    letter-spacing: normal !important;
    border: 1px dashed var(--v-outline-base) !important;
    border-radius: 12px !important;
    height: 46px !important;
}

@media (max-width: 600px) {
    .re-add-actions {
        grid-template-columns: 1fr;
    }
}

.ghost {
    opacity: 0.5;
}

.pfe-footer {
    display: grid;
    grid-template-columns: 1fr 2fr;
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
    overflow: hidden;
}

.pfe-fullscreen .pfe-footer>.v-btn {
    height: calc(52px + var(--safe-area-inset-bottom, 0px)) !important;
    padding-bottom: var(--safe-area-inset-bottom, 0px);
}

.pfe-footer>.v-btn {
    width: 100% !important;
    min-width: 0 !important;
    height: 52px !important;
    border-radius: 0 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.pfe-btn-cancel {
    background: var(--v-surface2-base) !important;
    border-right: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base) !important;
    font-size: 14px;
    font-weight: 600;
}

.pfe-btn-next {
    font-size: 15px;
    font-weight: 700;
}
</style>
